// Global unread-notification state: the small unread-only set + the one unified count used for
// both the bell and the OS badge (notification-center-and-badge-plan.md §5.1, §10.5). Mounted once
// near the app root so auto-ack (see NotificationAutoAck below) can fire from any route, not just
// while the inbox page itself is open.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { gql, useClient, useMutation, useSubscription } from 'urql';
import {
  AcknowledgeAllNotificationsMutation,
  AcknowledgeAllNotificationsMutationVariables,
  AcknowledgeNotificationsMutation,
  AcknowledgeNotificationsMutationVariables,
  FetchUnreadNotificationsQuery,
  FetchUnreadNotificationsQueryVariables,
  MyNotificationUpdateSubscription,
  MyNotificationUpdateSubscriptionVariables,
} from '../generated/hooks';
import { NotificationLogType } from '../generated/schemas';
import { setAppBadgeCount } from '../utils/appBadge';

const POLL_INTERVAL_MS = 60_000;

export const NOTIFICATION_FIELDS = gql`
  fragment NotificationFields on NotificationLogType {
    id
    category
    title
    body
    data
    created
    readAt
    chatUnreadCount
    league {
      id
      name
    }
  }
`;

const fetchUnreadNotifications = gql`
  query fetchUnreadNotifications {
    myNotifications(unreadOnly: true) {
      ...NotificationFields
    }
    myUnreadNotificationCount
  }
  ${NOTIFICATION_FIELDS}
`;

const acknowledgeNotificationsMutation = gql`
  mutation acknowledgeNotifications($ids: [String!]!) {
    acknowledgeNotifications(ids: $ids) {
      ok
    }
  }
`;

const acknowledgeAllNotificationsMutation = gql`
  mutation acknowledgeAllNotifications {
    acknowledgeAllNotifications {
      ok
    }
  }
`;

// Live per-user badge/bell sync. Deliberately its own subscription, not riding on leagueUpdate
// (notification data is per-user account data, and needs to reach every open tab regardless of
// which -- if any -- league that tab currently has subscribed). The yielded value is just the fresh
// unread count, not normalized-cache entities, so there's no serialize_*/footgun merge risk to
// manage here (see gql/graphql.py::resolve_my_notification_update). Treated purely as a wake-up
// signal: applied to the OS badge immediately for the fastest possible update, and also triggers a
// refetch of the full query below to reconcile the bell number and the unread list together.
const myNotificationUpdateSubscription = gql`
  subscription myNotificationUpdate {
    myNotificationUpdate
  }
`;

type NotificationsContextValue = {
  unreadNotifications: NotificationLogType[];
  unreadCount: number;
  loading: boolean;
  acknowledge: (ids: string[]) => Promise<void>;
  acknowledgeAll: () => Promise<void>;
  refetchUnread: () => void;
};

const NotificationsContext = createContext<NotificationsContextValue>({
  unreadNotifications: [],
  unreadCount: 0,
  loading: false,
  acknowledge: async () => undefined,
  acknowledgeAll: async () => undefined,
  refetchUnread: () => undefined,
});

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const client = useClient();
  const [state, setState] = useState<{ data: FetchUnreadNotificationsQuery | null; fetching: boolean }>({
    data: null,
    fetching: true,
  });
  const [, acknowledgeNotificationsMut] = useMutation<
    AcknowledgeNotificationsMutation,
    AcknowledgeNotificationsMutationVariables
  >(acknowledgeNotificationsMutation);
  const [, acknowledgeAllMut] = useMutation<
    AcknowledgeAllNotificationsMutation,
    AcknowledgeAllNotificationsMutationVariables
  >(acknowledgeAllNotificationsMutation);

  // Self-serializing fetch: at most one request in flight at a time. This exists because we now have
  // FIVE independent triggers for "go get the current unread state" (initial load, the 60s poll,
  // window focus, the live subscription below, and every acknowledge mutation's own post-mutation
  // refetch) -- and network responses can arrive out of order. A plain "just call refetch()" design
  // bit us twice already: whichever response lands LAST wins regardless of which one is actually
  // fresher, so a slow refetch triggered before an acknowledge can resolve after it and stomp the
  // correct (read) state with the stale (still-unread) snapshot it started from. Queuing instead of
  // stacking concurrent requests means every fetch that actually runs reflects the true state at the
  // moment it started, and responses can no longer arrive in the wrong order because there's only
  // ever one in flight.
  const inFlightRef = useRef<Promise<void> | null>(null);
  const queuedRef = useRef(false);

  const runFetch = useCallback(async () => {
    const result = await client
      .query<FetchUnreadNotificationsQuery, FetchUnreadNotificationsQueryVariables>(
        fetchUnreadNotifications,
        {},
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    if (result.data) setState({ data: result.data, fetching: false });
  }, [client]);

  const refetchUnread = useCallback(() => {
    if (inFlightRef.current) {
      // Something's already running -- one follow-up run after it finishes is enough to pick up
      // everything that happened in the meantime; no need to stack a request per trigger.
      queuedRef.current = true;
      return;
    }
    const run = () => {
      inFlightRef.current = runFetch().finally(() => {
        inFlightRef.current = null;
        if (queuedRef.current) {
          queuedRef.current = false;
          run();
        }
      });
    };
    run();
  }, [runFetch]);

  // Initial load.
  useEffect(() => {
    refetchUnread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll + refetch on focus as a FALLBACK -- the subscription below is the primary, near-instant
  // path, but this covers the gap while the WS is (re)connecting and protects against a dropped
  // connection going unnoticed.
  useEffect(() => {
    const interval = setInterval(refetchUnread, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchUnread();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refetchUnread);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refetchUnread);
    };
  }, [refetchUnread]);

  // Live sync (notification-center-and-badge-plan.md addendum): apply the pushed count to the OS
  // badge immediately -- no round trip needed for that part, the server already computed it fresh at
  // publish time -- and also refetch the full query so the bell number and unread list (used by
  // NotificationAutoAck's matching) reconcile moments later. The refetch is intentionally not
  // skipped just because we already have a fresh count: the count alone can't tell us WHICH
  // notification changed, and the list needs that.
  const [{ data: subscriptionData }] = useSubscription<
    MyNotificationUpdateSubscription,
    MyNotificationUpdateSubscription,
    MyNotificationUpdateSubscriptionVariables
  >({ query: myNotificationUpdateSubscription });
  useEffect(() => {
    if (typeof subscriptionData?.myNotificationUpdate !== 'number') return;
    setAppBadgeCount(subscriptionData.myNotificationUpdate);
    refetchUnread();
  }, [subscriptionData, refetchUnread]);

  const unreadNotifications = (state.data?.myNotifications ?? []) as NotificationLogType[];
  const unreadCount = state.data?.myUnreadNotificationCount ?? 0;

  // Foreground badge wiring (§6.1) -- mirrors myUnreadNotificationCount exactly, the same number the
  // bell shows. A no-op outside an installed PWA (see appBadge.ts's feature detection).
  useEffect(() => {
    setAppBadgeCount(unreadCount);
  }, [unreadCount]);

  const acknowledge = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      await acknowledgeNotificationsMut({ ids });
      refetchUnread();
    },
    [acknowledgeNotificationsMut, refetchUnread],
  );

  const acknowledgeAll = useCallback(async () => {
    await acknowledgeAllMut({});
    refetchUnread();
  }, [acknowledgeAllMut, refetchUnread]);

  return (
    <NotificationsContext.Provider
      value={{
        unreadNotifications,
        unreadCount,
        loading: state.fetching,
        acknowledge,
        acknowledgeAll,
        refetchUnread,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  return useContext(NotificationsContext);
}

function findAutoAckMatches(currentPath: string, list: NotificationLogType[]): string[] {
  return list
    .filter(n => !n.id.startsWith('chat:'))
    .filter(n => {
      const ctaUrl = (n.data as { ctaUrl?: string } | null)?.ctaUrl;
      if (!ctaUrl) return false;
      let targetPath: string;
      try {
        targetPath = new URL(ctaUrl).pathname;
      } catch {
        return false;
      }
      return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
    })
    .map(n => n.id);
}

// Path-prefix auto-ack (§5.2): a notification is acknowledged when the current route equals its
// target path, or is nested under it (currentPath === targetPath || currentPath.startsWith(targetPath
// + "/")). Pins are excluded -- they clear themselves independently via chat's own MarkChatRead calls
// when the chat panel is viewed (Chat/index.tsx), not through this generic mechanism.
//
// Two effects, deliberately split by trigger, not merged into one -- two earlier attempts each had a
// real bug:
//   1. A "refetch on every navigation" effect running independently alongside a separate
//      "match-and-acknowledge" effect raced: the plain refetch has no mutation to wait for, so it can
//      resolve AFTER acknowledge()'s own post-mutation refetch and stomp the fresh (read) state with
//      the stale (still-unread) snapshot it started from -- order between two independent network
//      round trips is never guaranteed.
//   2. Merging them into one effect that calls refetchUnread() whenever nothing matches, keyed on
//      unreadNotifications, loops: that refetch produces a new unreadNotifications reference even
//      when the content is unchanged, which re-triggers the very same effect, forever.
// The fix is to separate "when do we trigger a lookup" from "when do we re-check for a match":
//   - Effect A fires ONLY on navigation. It checks whatever's already loaded (read via a ref, so this
//     effect never depends on unreadNotifications and can't loop). A local match acknowledges
//     immediately -- no separate racing refetch alongside it. No local match falls back to ONE
//     refetch, to catch a notification this tab doesn't know about yet.
//   - Effect B fires whenever fresh unread data arrives, from ANY source (poll, focus, Effect A's
//     catch-up refetch, or an acknowledge mutation's own refetch), and re-checks the CURRENT route
//     (read via a ref) against it. It only ever calls acknowledge(), never refetchUnread() itself --
//     acknowledging strictly shrinks the unread set, so repeated firing converges to a no-op instead
//     of looping, unlike a bare refetch would.
export function NotificationAutoAck() {
  const location = useLocation();
  const { unreadNotifications, acknowledge, refetchUnread } = useNotificationsContext();
  const acknowledgeRef = useRef(acknowledge);
  acknowledgeRef.current = acknowledge;
  const refetchUnreadRef = useRef(refetchUnread);
  refetchUnreadRef.current = refetchUnread;
  const unreadNotificationsRef = useRef(unreadNotifications);
  unreadNotificationsRef.current = unreadNotifications;
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    const matchingIds = findAutoAckMatches(location.pathname, unreadNotificationsRef.current);
    if (matchingIds.length > 0) {
      acknowledgeRef.current(matchingIds);
    } else {
      refetchUnreadRef.current();
    }
  }, [location.pathname]);

  useEffect(() => {
    const matchingIds = findAutoAckMatches(pathRef.current, unreadNotifications);
    if (matchingIds.length > 0) acknowledgeRef.current(matchingIds);
  }, [unreadNotifications]);

  return null;
}
